import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import SEO from './SEO';
import './ProductsPage.css';

const ProductsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { addToCart } = useCart();
  const { products, getCategories, searchProducts, getProductsByCategory } = useProducts();

  // Filtrer les produits selon la catégorie et le terme de recherche
  const getFilteredProducts = () => {
    let filtered = products;
    
    // Filtrer par catégorie
    if (selectedCategory !== 'Tous') {
      filtered = getProductsByCategory(selectedCategory);
    }
    
    // Filtrer par recherche
    if (searchTerm) {
      filtered = searchProducts(searchTerm).filter(product => 
        selectedCategory === 'Tous' || product.category === selectedCategory
      );
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Trier les produits
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'category':
        return a.category.localeCompare(b.category);
      default:
        return 0;
    }
  });

  const handleAddToCart = (product) => {
    console.log('🛒 ProductsPage - Ajout au panier:', product?.name);
    if (!product) {
      console.error('❌ Produit invalide dans ProductsPage');
      return;
    }
    // Vérifier si le produit est en rupture de stock
    if (product.stock === 0) {
      console.warn('⚠️ Produit en rupture de stock:', product.name);
      return;
    }
    addToCart(product, 1);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  return (
    <>
      <SEO 
        title="Nos Produits - Catalogue de Viandes Bio"
        description="Découvrez notre catalogue complet de viandes bio de qualité supérieure. Bœuf bio, porc bio, agneau bio, volaille bio et charcuterie bio. Produits locaux au Lude (72800)."
        keywords="catalogue viande bio, bœuf bio, porc bio, agneau bio, volaille bio, charcuterie bio, produits bio Le Lude, viande bio 72800, achat viande bio"
        canonical="https://lafermeancienne.fr/produits"
      />
      <main className="products-page">
        <div className="container">
          <header className="products-header">
            <h1>Nos Produits</h1>
          </header>


        {/* Filtres et recherche */}
        <div className="products-controls">
          <div className="search-section">
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filters-section">
            <div className="filter-group">
              <label htmlFor="category-filter">Catégorie :</label>
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {getCategories().map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="sort-filter">Trier par :</label>
              <select
                id="sort-filter"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="name">Nom (A-Z)</option>
                <option value="price-low">Prix (croissant)</option>
                <option value="price-high">Prix (décroissant)</option>
                <option value="category">Catégorie</option>
              </select>
            </div>
          </div>
        </div>

        {/* Résultats */}
        <div className="products-results">
          <div className="results-info">
            <p>
              {sortedProducts.length} produit{sortedProducts.length > 1 ? 's' : ''} trouvé{sortedProducts.length > 1 ? 's' : ''}
              {selectedCategory !== 'Tous' && ` dans la catégorie "${selectedCategory}"`}
            </p>
          </div>

          {/* Grille des produits */}
          <div className="products-grid">
            {sortedProducts.length > 0 ? (
              sortedProducts.map(product => (
                <div key={product.id} className="product-item">
                  <ProductCard 
                    product={product} 
                    onAddToCart={handleAddToCart}
                    onProductClick={handleProductClick}
                    showFullDescription={true}
                  />
                </div>
              ))
            ) : (
              <div className="no-products">
                <div className="no-products-content">
                  <h3>Aucun produit trouvé</h3>
                  <p>
                    {searchTerm 
                      ? `Aucun produit ne correspond à votre recherche "${searchTerm}"`
                      : `Aucun produit dans la catégorie "${selectedCategory}"`
                    }
                  </p>
                  <button 
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('Tous');
                    }}
                    className="reset-filters-btn"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        </div>
      </main>

      {/* Modal de visualisation de produit */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default ProductsPage;
